import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { TbMoneybag } from "react-icons/tb";
import { BsCashStack } from "react-icons/bs";
import Repayments from "@mui/icons-material/Payments";
import { RiMoneyRupeeCircleFill } from "react-icons/ri";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { Box, capitalize, Grid } from "@mui/material";
import { motion } from "framer-motion";
import React, { useEffect } from "react";
import WSDashboardCard from "../../Components/WSDashboardCard";
import { useDashboard } from "../../StateManagement/dashboardContext";
import { useNavigate } from "react-router-dom";
import Loader from "./Loader";
import DashboardTables from "./DashboardTables";
import bgImage from "../../images/DashboardFrame.png";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";

const KPIAnimation = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6 } },
};

const slideInAnimation = {
  hidden: { opacity: 0, x: -100 }, // Initially off-screen to the left
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } },
};

export const DashBoard: React.FC<any> = React.memo((props) => {
  const { fetchDashboardData, loading, kpis } = useDashboard();
  const navigate = useNavigate();

  // Function to format the amount correctly with commas and currency symbol
  const formatCurrency = (amount: number) => {
    if (!amount) return "₹0.00"; // Default to "$0.00" if no amount
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(amount); // Adjust for other currencies if needed
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const statusColors: Record<
    string,
    {
      color: string;
      bgColor: string;
    }
  > = {
    sanctioned: {
      color: "#28a745", // green
      bgColor: "#e6f4ea",
    },
    pending: {
      color: "#ffc107", // yellow
      bgColor: "#fff9e6",
    },
    rejected: {
      color: "#dc3545", // red
      bgColor: "#fbeaea",
    },
    completed: {
      color: "#6c757d", // grey
      bgColor: "#f2f2f2",
    },
    default: {
      color: "#6f42c1", // purple
      bgColor: "#f1e6fa",
    },
  };

  //  useEffect(() => {
  //   updateMainHeader("District");
  // });

  breadCrumbsStore.updateBreadCrumbs([{ "Overview": "/dashboard" }]);
  
  return (
    <>
      <Box
        sx={{
          background: `url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          padding: 2,
          borderRadius: 2,
        }}
      >
        {" "}
        <Loader loading={loading} />
        <Grid container spacing={2}>
          <Grid item sm={3}>
            <motion.div
              variants={KPIAnimation}
              initial="hidden"
              animate="visible"
            >
              <WSDashboardCard
                title="Total Loans"
                titleColor="#022324"
                value={kpis.totalLoans}
                percentage="+2.5%"
                percentageColor="#098963"
                bottomIcon={<TrendingUpIcon style={{ color: "#098963" }} />}
                icon={<TbMoneybag style={{ color: "#022324" }} />}
                onClick={() => navigate(`/loan/requests/list/all`)}
                redirect
              />
            </motion.div>
          </Grid>

          <Grid item sm={3}>
            <motion.div
              variants={KPIAnimation}
              initial="hidden"
              animate="visible"
            >
              <WSDashboardCard
                title="Total Loan Amount"
                titleColor="#022324"
                value={formatCurrency(kpis.totalLoanAmount)}
                percentage="+2.5%"
                percentageColor="#098963"
                bottomIcon={<TrendingUpIcon style={{ color: "#098963" }} />}
                icon={<BsCashStack style={{ color: "#022324" }} />}
              />
            </motion.div>
          </Grid>

          <Grid item sm={3}>
            <motion.div
              variants={KPIAnimation}
              initial="hidden"
              animate="visible"
            >
              <WSDashboardCard
                title="Total Repayment Amount"
                titleColor="#022324"
                value={formatCurrency(kpis.totalRepaymentAmount)}
                percentage={"+1"}
                bottomIcon={<TrendingUpIcon />}
                icon={<RiMoneyRupeeCircleFill style={{ color: "#022324" }} />}
              />
            </motion.div>
          </Grid>

          <Grid item sm={3}>
            <motion.div
              variants={KPIAnimation}
              initial="hidden"
              animate="visible"
            >
              <WSDashboardCard
                title="Today Loans"
                titleColor="#022324"
                value={kpis.loansToday.count}
                percentage={kpis.loansToday.percentage}
                percentageColor="#098963"
                bottomIcon={<TrendingUpIcon style={{ color: "#098963" }} />}
                icon={<TbMoneybag style={{ color: "#022324" }} />}
                onClick={() =>
                  navigate(`/loan/requests/list/filter?createdToday=true`)
                }
                redirect
              />
            </motion.div>
          </Grid>
          
          <Grid item sm={3}>
            <motion.div
              variants={KPIAnimation}
              initial="hidden"
              animate="visible"
            >
              <WSDashboardCard
                title="Today Repayment Amount"
                titleColor="#022324"
                value={formatCurrency(kpis.repaymentAmountToday.count)}
                percentage={kpis.repaymentAmountToday.percentage}
                percentageColor="#098963"
                bottomIcon={<TrendingUpIcon style={{ color: "#098963" }} />}
                icon={<RiMoneyRupeeCircleFill style={{ color: "#022324" }} />}
                onClick={() => navigate(`/loan/repayment/requests/list/filter`)}
                redirect
              />
            </motion.div>
          </Grid>

          {/* <Grid item sm={3}>
                    <WSDashboardCard
                        title="Total Interest Profit"
                        value={kpis.totalInterest}
                        percentage="+2.5%"
                        bottomIcon={<TrendingUpIcon />}
                        icon={<LoanIcon sx={{ color: '#a71a68' }} />}
                        onClick={() => navigate(`/loan/requests/list/all`)}
                        redirect
                    />
                </Grid> */}

          {kpis.loanStatusCounts?.map((item: any) => {
            const colors =
              statusColors[item._id?.toLowerCase()] || statusColors.default;

            return (
              <Grid item sm={3} key={item._id}>
                <motion.div
                  variants={slideInAnimation}
                  initial="hidden"
                  animate="visible"
                >
                  <WSDashboardCard
                    title={`Status: ${capitalize(item._id)}`}
                    titleColor="#022324"
                    value={item?.count}
                    percentage={item.percentage}
                    percentageColor="#098963"
                    icon={<DoneAllIcon style={{ color: "#022324" }} />}
                    bottomIcon={<TrendingUpIcon style={{ color: "#098963" }} />}
                    onClick={() =>
                      navigate(
                        `/loan/requests/list/filter?status=${item._id?.toLowerCase()}`
                      )
                    }
                    redirect
                  />
                </motion.div>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <DashboardTables loading={loading} />
    </>
  );
});